import React, { Fragment, useMemo } from 'react';
import Currency from 'react-currency-formatter';
import { useTranslation } from 'react-i18next';

// material-ui
import { TableCell, TableRow } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Warning } from '@mui/icons-material';
import { useLanguageReference } from 'services/Hooks/useLanguageReference';
import { useAllInventoryItems } from 'services/Data/InventoryItemServices';
import { arrayUnique } from 'services/Utilities/Utilities';
import InventoryItemCategoryEnum, { InventoryItemCategoryEnumValue } from 'enums/InventoryItemCategoryEnum';
import ThemedTooltip from '@acnb/CustomComponents/Tooltip/ThemedTooltip';

// Icons

// Styles

// Actions

// Components

// Others

const useStyles = makeStyles(theme => ({
  //...customGlobalStyle(theme),
  projectWallCell: {
    maxWidth: '180px',
    textTransform: 'uppercase',
    borderRight: '1px solid rgba(224, 224, 224, 1)',
    fontSize: 'medium',
  },
  packagePrice: {
    borderLeft: '1px solid rgba(224, 224, 224, 1)',
    fontSize: 'medium',
    textAlign: 'center',
  },
  tableRow: {
    '& td': {
      padding: '10px',
    },
  },
  projectWallBottomBorder: {
    borderBottom: '0.15rem solid rgba(169, 169, 169, 1)',
  },
  projectWallLeftBorder: {
    borderLeft: '0.15rem solid rgba(169, 169, 169, 1)',
  },
  projectWallTopBorder: {
    borderTop: '0.15rem solid rgba(169, 169, 169, 1)',
  },
  projectWallRightBorder: {
    borderRight: '0.15rem solid rgba(169, 169, 169, 1)',
  },
}));

const DealerCopyGroupedProjectWallSection = ({
  projectInventoryItems,
  projectWalls,
  headerColSpan,
  projectCurrency,
  setWarningModalInfo,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { labelReference, descriptionReference, warningReference, warningReferenceFallback } = useLanguageReference();

  const { data: availableInventoryItems } = useAllInventoryItems();

  const isFrench = t('locale') === 'fr';
  const panelTypeLabel = isFrench ? 'panelTypeNameFr' : 'panelTypeNameEn';
  const panelHeaderLabel = isFrench ? 'panelHeaderNameFr' : 'panelHeaderNameEn';

  const projectWallInventoryItemMap = useMemo(() => {
    const map = {};
    // eslint-disable-next-line no-unused-expressions
    projectInventoryItems?.forEach(ii => {
      if (!map[ii.projectWall_Id]) {
        map[ii.projectWall_Id] = [];
      }
      map[ii.projectWall_Id].push(ii);
    });

    return map;
  }, [projectInventoryItems]);

  return (
    <>
      <TableRow>
        <TableCell colSpan={headerColSpan} style={{ backgroundColor: '#c9c7c7' }}>
          <strong>{t('walls')}</strong>
        </TableCell>
      </TableRow>
      {projectWalls?.map((projectWall, projectWallIndex) => {
        const currentProjectWallProjectInventoryItems = projectWallInventoryItemMap[projectWall.id]?.filter(
          projectInventoryItem => projectInventoryItem.quantity,
        );

        const totalForSection = currentProjectWallProjectInventoryItems?.reduce(
          (acc, cur) => acc + cur.projectDealerTotalPrice,
          0,
        );

        const projectWallInventoryItemCategories = arrayUnique(
          currentProjectWallProjectInventoryItems?.map(i => i.inventoryItemCategory) || [],
        );

        const totalRowSpan =
          (currentProjectWallProjectInventoryItems?.length || 0) + (projectWallInventoryItemCategories?.length || 0);

        return projectWallInventoryItemCategories
          .sort((a, b) => a - b)
          .map((categoryEnum, catIndex) => {
            let category = InventoryItemCategoryEnum.find(ii => ii.value === Number(categoryEnum));
            let categoryLabel = category?.[labelReference];

            const currentProjectWallCategoryItems = currentProjectWallProjectInventoryItems?.filter(
              i => i.inventoryItemCategory === categoryEnum,
            );

            const isLastCategory = projectWallInventoryItemCategories.length - 1 === catIndex;

            return (
              <Fragment key={categoryEnum}>
                <TableRow>
                  {catIndex === 0 && (
                    <TableCell
                      className={`${classes.projectWallCell} ${classes.projectWallBottomBorder} ${classes.projectWallTopBorder} ${classes.projectWallLeftBorder}`}
                      rowSpan={totalRowSpan}>
                      {`${t('section')} ${projectWallIndex + 1}:`}
                      <br />
                      {`${projectWall[panelTypeLabel]} (${projectWall.numberOfPanels} ${t('panels')}),`}
                      <br />
                      {`${projectWall[panelHeaderLabel]}`}
                      <br />
                      {`${projectWall.note || ''}`}
                    </TableCell>
                  )}
                  {categoryEnum === InventoryItemCategoryEnumValue.Graphic ? (
                    <TableCell
                      className={catIndex === 0 ? classes.projectWallTopBorder : ''}
                      colSpan={headerColSpan - 2}
                      style={{ backgroundColor: '#c9c7c7' }}>
                      <strong>{categoryLabel}</strong>
                    </TableCell>
                  ) : (
                    <TableCell
                      className={catIndex === 0 ? classes.projectWallTopBorder : ''}
                      colSpan={headerColSpan - 2}
                      style={{ maxHeight: 0, height: 0, padding: 0 }}></TableCell>
                  )}

                  {catIndex === 0 && (
                    <TableCell
                      className={`${classes.packagePrice} ${classes.projectWallBottomBorder} ${classes.projectWallTopBorder} ${classes.projectWallRightBorder}`}
                      rowSpan={totalRowSpan}>
                      <Currency quantity={totalForSection} currency={projectCurrency} />
                    </TableCell>
                  )}
                </TableRow>

                {currentProjectWallCategoryItems?.map((projectInventoryItem, itemIndex) => {
                  let inventoryItem = availableInventoryItems?.find(as => as.id === projectInventoryItem.inventoryItem_Id);
                  const isLastItem = currentProjectWallCategoryItems.length - 1 === itemIndex;

                  return (
                    <TableRow className={`${classes.tableRow}`} key={projectInventoryItem.id}>
                      <TableCell className={isLastCategory && isLastItem ? classes.projectWallBottomBorder : ''}>
                        {(inventoryItem?.[warningReference] || inventoryItem?.[warningReferenceFallback]) && (
                          <ThemedTooltip
                            title={inventoryItem?.[warningReference] || inventoryItem?.[warningReferenceFallback]}
                            placement={'top'}>
                            <Warning
                              color="warning"
                              style={{ cursor: 'pointer' }}
                              sx={{ mr: 1 }}
                              onClick={() => {
                                setWarningModalInfo({ open: true, inventoryItem });
                              }}
                            />
                          </ThemedTooltip>
                        )}
                        <strong>{`${inventoryItem?.sku}`}</strong>
                      </TableCell>
                      <TableCell className={isLastCategory && isLastItem ? classes.projectWallBottomBorder : ''}>
                        {projectInventoryItem?.[descriptionReference]}
                      </TableCell>

                      <TableCell
                        className={isLastCategory && isLastItem ? classes.projectWallBottomBorder : ''}
                        align="center">
                        {projectInventoryItem.quantity}
                      </TableCell>

                      <TableCell
                        className={isLastCategory && isLastItem ? classes.projectWallBottomBorder : ''}
                        align="right">
                        {projectInventoryItem.projectDealerUnitPrice === 0 ? (
                          '-'
                        ) : (
                          <Currency quantity={projectInventoryItem.projectDealerUnitPrice} currency={projectCurrency} />
                        )}
                      </TableCell>
                      <TableCell
                        className={isLastCategory && isLastItem ? classes.projectWallBottomBorder : ''}
                        align="right">
                        {projectInventoryItem.projectDealerTotalPrice === 0 ? (
                          '-'
                        ) : (
                          <Currency quantity={projectInventoryItem.projectDealerTotalPrice} currency={projectCurrency} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </Fragment>
            );
          });
      })}
    </>
  );
};

export default DealerCopyGroupedProjectWallSection;
